import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const articlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (articlesRef.current && !articlesRef.current.contains(e.target as Node)) {
        setArticlesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          {isHomePage ? (
            <h1 className="contents">
              <Link
                to="/"
                className={`font-heading text-xl font-bold tracking-tight whitespace-nowrap transition-colors ${
                  scrolled ? 'text-foreground-900' : 'text-white'
                }`}
              >
                VigorBuy Spreadsheet
              </Link>
            </h1>
          ) : (
            <Link
              to="/"
              className={`font-heading text-xl font-bold tracking-tight whitespace-nowrap transition-colors ${
                scrolled ? 'text-foreground-900' : 'text-white'
              }`}
            >
              VigorBuy Spreadsheet
            </Link>
          )}

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={articlesRef} className="relative">
              <button
                onClick={() => setArticlesOpen(!articlesOpen)}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Articles
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${articlesOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {articlesOpen && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 max-h-[420px] overflow-y-auto">
                  {articles.map((art) => (
                    <Link
                      key={art.seq}
                      to={`/${art.slug}`}
                      className="flex items-start gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      onClick={() => setArticlesOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 flex-shrink-0 mt-0.5">
                        <i className={`${art.icon} text-sm`}></i>
                      </span>
                      <span className="leading-snug text-xs font-medium line-clamp-2">{art.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/vigorbuy-spreadsheet-guide"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Guide
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold rounded-full border-2 border-white/60 text-white hover:bg-white hover:text-foreground-900 transition-all whitespace-nowrap cursor-pointer"
            >
              VigorBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-home-line"></i>
              </span>
              Home
            </Link>

            <div className="border-t border-background-200 mt-1 pt-3">
              <button
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-900 uppercase tracking-wide rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {mobileCategoriesOpen && (
                <div className="flex flex-col gap-0.5 pl-11 mt-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200 mt-1 pt-3">
              <button
                onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-900 uppercase tracking-wide rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    <i className="ri-article-line"></i>
                  </span>
                  Articles
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform ${mobileArticlesOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {mobileArticlesOpen && (
                <div className="flex flex-col gap-0.5 pl-11 mt-1">
                  {articles.map((art) => (
                    <Link
                      key={art.seq}
                      to={`/${art.slug}`}
                      className="flex items-start gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors leading-snug"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 flex-shrink-0 mt-0.5">
                        <i className={`${art.icon} text-xs`}></i>
                      </span>
                      <span className="text-xs leading-relaxed">{art.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-guide-line"></i>
              </span>
              Guide
            </Link>

            <div className="border-t border-background-200 mt-3 pt-4 flex flex-col gap-2.5">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center px-5 py-3 text-sm font-semibold rounded-full border-2 border-primary-500 text-primary-600 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
              >
                VigorBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center px-5 py-3 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}